import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSave, faFileAlt, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch, MODULE_TYPE_BADGE, MODULE_TYPE_LABELS } from '../../utils/http';
import StatementModal from '../results/StatementModal';
import type { StatementData } from '../results/StatementModal';

interface ModuleCol {
  moduleId: string;
  title: string;
  moduleType: number;
  orderIndex: number;
}

interface MarkCell {
  moduleId: string;
  mark: number;
  comment: string | null;
}

interface StudentRow {
  studentId: string;
  studentName: string;
  studentIdNumber: string;
  marks: MarkCell[];
  latestStatementId: string | null;
  latestStatementNumber: string | null;
}

interface CourseMarks {
  learnershipTitle: string;
  passMark: number;
  modules: ModuleCol[];
  students: StudentRow[];
}

const key = (studentId: string, moduleId: string) => `${studentId}|${moduleId}`;

/**
 * Trainer/assessor mark capture: one mark (0-100) per learner per module,
 * plus generation of a statement of results once every module has a mark.
 */
export default function ModuleMarksPanel({ learnershipId }: { learnershipId: string }) {
  const { accessToken } = useAuth();
  const [data, setData] = useState<CourseMarks | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Edited values as strings, keyed by student|module. Absent key = unchanged.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [issuingFor, setIssuingFor] = useState<string | null>(null);
  const [statement, setStatement] = useState<StatementData | null>(null);
  const [openStatementId, setOpenStatementId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    const res = await authFetch<CourseMarks>(accessToken, `/api/marks/course/${learnershipId}`);
    if (res.ok && res.data) {
      setData(res.data);
      setError('');
    } else {
      setError(res.message || 'Failed to load marks.');
    }
    setEdits({});
    setIsLoading(false);
  }, [accessToken, learnershipId]);

  useEffect(() => {
    load();
  }, [load]);

  const saved = useMemo(() => {
    const map = new Map<string, MarkCell>();
    data?.students.forEach((s) => s.marks.forEach((m) => map.set(key(s.studentId, m.moduleId), m)));
    return map;
  }, [data]);

  const valueFor = (studentId: string, moduleId: string) => {
    const k = key(studentId, moduleId);
    if (k in edits) return edits[k];
    const cell = saved.get(k);
    return cell ? String(cell.mark) : '';
  };

  const setValue = (studentId: string, moduleId: string, value: string) => {
    setNotice('');
    const cell = saved.get(key(studentId, moduleId));
    const original = cell ? String(cell.mark) : '';
    setEdits((prev) => {
      const next = { ...prev };
      if (value === original) delete next[key(studentId, moduleId)];
      else next[key(studentId, moduleId)] = value;
      return next;
    });
  };

  const invalidEdit = Object.values(edits).some((v) => v !== '' && (!/^\d+$/.test(v) || Number(v) > 100));
  const dirtyCount = Object.keys(edits).length;

  const saveAll = async () => {
    setError('');
    setNotice('');
    const entries = Object.entries(edits).map(([k, v]) => {
      const [studentId, moduleId] = k.split('|');
      return { studentId, moduleId, mark: v === '' ? null : Number(v), comment: saved.get(k)?.comment ?? null };
    });
    setIsSaving(true);
    const res = await authFetch(accessToken, `/api/marks/course/${learnershipId}`, { method: 'POST', body: { entries } });
    setIsSaving(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setNotice('Marks saved.');
    load();
  };

  const issue = async (studentId: string) => {
    setError('');
    setNotice('');
    if (dirtyCount > 0) {
      setError('Save your changes before generating a statement.');
      return;
    }
    setIssuingFor(studentId);
    const res = await authFetch<{ data: StatementData }>(accessToken, `/api/marks/course/${learnershipId}/students/${studentId}/statement`, {
      method: 'POST',
    });
    setIssuingFor(null);
    if (!res.ok || !res.data?.data) {
      setError(res.message || 'Could not generate the statement.');
      return;
    }
    setStatement(res.data.data);
    load();
  };

  if (isLoading && !data) {
    return (
      <div className="flex justify-center py-12">
        <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>;
  }

  if (data.modules.length === 0) {
    return (
      <div className="card-surface p-12 text-center">
        <p className="text-brand-900/60">This course has no modules yet. Ask an administrator to add them.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-brand-900/60">
          Capture a mark out of 100 for each module. A module is Competent at {data.passMark}% or above.
        </p>
        <button
          onClick={saveAll}
          disabled={isSaving || dirtyCount === 0 || invalidEdit}
          className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
        >
          <FontAwesomeIcon icon={isSaving ? faSpinner : faSave} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
          Save marks{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
        </button>
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}
      {notice && (
        <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} className="h-4 w-4" />
          {notice}
        </p>
      )}
      {invalidEdit && <p className="text-sm text-amber-700">Marks must be whole numbers from 0 to 100.</p>}

      {data.students.length === 0 ? (
        <div className="card-surface p-12 text-center">
          <p className="text-brand-900/60">No learners are enrolled in this course yet.</p>
        </div>
      ) : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-canvas-50 text-left text-xs uppercase tracking-wider text-brand-900/50">
                <th className="px-4 py-3 min-w-[180px]">Learner</th>
                {data.modules.map((m) => (
                  <th key={m.moduleId} className="px-3 py-3 min-w-[130px]">
                    <span className="block normal-case tracking-normal text-brand-950 font-semibold text-xs">{m.title}</span>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${MODULE_TYPE_BADGE[m.moduleType]}`}>
                      {MODULE_TYPE_LABELS[m.moduleType]}
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3">Statement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-canvas-200">
              {data.students.map((s) => {
                const allCaptured = data.modules.every((m) => valueFor(s.studentId, m.moduleId) !== '');
                return (
                  <tr key={s.studentId}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-brand-950">{s.studentName}</p>
                      <p className="text-xs text-brand-900/50">{s.studentIdNumber}</p>
                    </td>
                    {data.modules.map((m) => {
                      const v = valueFor(s.studentId, m.moduleId);
                      const num = v === '' ? null : Number(v);
                      const changed = key(s.studentId, m.moduleId) in edits;
                      return (
                        <td key={m.moduleId} className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              inputMode="numeric"
                              value={v}
                              onChange={(e) => setValue(s.studentId, m.moduleId, e.target.value.trim())}
                              className={`w-16 px-2 py-1.5 border rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                                changed ? 'border-brand-500 bg-brand-50' : 'border-canvas-300'
                              }`}
                              aria-label={`${s.studentName} ${m.title} mark`}
                            />
                            {num != null && !Number.isNaN(num) && (
                              <span className={`text-[10px] font-bold uppercase ${num >= data.passMark ? 'text-green-700' : 'text-red-700'}`}>
                                {num >= data.passMark ? 'C' : 'NYC'}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5">
                        <button
                          onClick={() => issue(s.studentId)}
                          disabled={!allCaptured || issuingFor === s.studentId}
                          title={allCaptured ? 'Generate statement of results' : 'Capture and save marks for every module first'}
                          className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors disabled:opacity-40 flex items-center gap-1.5 w-fit"
                        >
                          <FontAwesomeIcon icon={issuingFor === s.studentId ? faSpinner : faFileAlt} className={`h-3 w-3 ${issuingFor === s.studentId ? 'animate-spin' : ''}`} />
                          Generate
                        </button>
                        {s.latestStatementId && (
                          <button onClick={() => setOpenStatementId(s.latestStatementId)} className="text-xs font-semibold text-brand-700 hover:text-brand-800 text-left">
                            View {s.latestStatementNumber}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {statement && <StatementModal statement={statement} onClose={() => setStatement(null)} />}
      {openStatementId && <StatementModal statementId={openStatementId} onClose={() => setOpenStatementId(null)} />}
    </div>
  );
}
